// The board's vocabulary: sides, pieces, packed squares, directions, names.
//
// **Shared by both trees and owned by neither** (PLAN-CLIENT-SPLIT §1, day
// 4). These are the numbers a piece, a square and a direction *are* on the
// wire, in a save and in a fixture — so the engine defines its rules over
// them and the client keys its meshes and yaw tables by them, and neither
// imports the other to do it. `src/engine/rules/board.ts` re-exports the lot,
// so every engine importer is unchanged.
//
// **This folder imports nothing under `src/`** — `eslint.config.js` makes
// that an error. Nothing here reads state or decides a rule.

/**
 * An army. `Side` is a number rather than `0 | 1`: a run can hold eight
 * armies (`MAX_SIDES`), and most of the code is still talking about a
 * two-player game.
 */
export type Side = number;
export type Color = Side;
export const WHITE: Side = 0;
export const BLACK: Side = 1;

/** Sides in a classic game. `Position.sides` is the real count. */
export const CLASSIC_SIDES = 2;

/** Three bits of side in a packed piece, so this many armies at most. */
export const MAX_SIDES = 8;

/**
 * The type field is three bits (`side << 3 | type`). **7 is retired and left
 * unused** — it was the monster (§31 R2); a wire value is never renumbered.
 */
export type PieceType = 1 | 2 | 3 | 4 | 5 | 6;
export const PAWN = 1;
export const KNIGHT = 2;
export const BISHOP = 3;
export const ROOK = 4;
export const QUEEN = 5;
export const KING = 6;
/** 0 = empty, otherwise `color << 3 | type`. */
export type Piece = number;
export const EMPTY = 0;

/**
 * `side << 3 | type`. Three bits of side, so up to eight armies — well past
 * anything the board can hold a distinguishable set of pieces for.
 */
export const makePiece = (side: Side, type: PieceType): Piece => (side << 3) | type;
export const pieceType = (p: Piece): PieceType => (p & 7) as PieceType;
export const pieceColor = (p: Piece): Side => (p >> 3) & 7;
export const pieceSide = pieceColor;

// --- packed coordinates ----------------------------------------------------

/** Bits per rank row. 1024 files per rank keeps a square inside a 32-bit SMI. */
export const SHIFT = 10;
export const STRIDE = 1 << SHIFT;
export const FILE_MASK = STRIDE - 1;
/** Coordinate origin, so negative files and ranks pack without going negative. */
export const BIAS = STRIDE >> 1;

/** Smallest and largest coordinate the packing can hold. */
export const COORD_MIN = -BIAS;
export const COORD_MAX = BIAS - 1;

export const square = (file: number, rank: number): number =>
  ((rank + BIAS) << SHIFT) | (file + BIAS);
export const fileOf = (sq: number): number => (sq & FILE_MASK) - BIAS;
export const rankOf = (sq: number): number => (sq >>> SHIFT) - BIAS;

/** An inclusive rectangle of files and ranks. */
export interface Bounds {
  minFile: number;
  maxFile: number;
  minRank: number;
  maxRank: number;
}

/** The classic board: a1 to h8. */
export const CLASSIC_BOUNDS: Bounds = { minFile: 0, maxFile: 7, minRank: 0, maxRank: 7 };

/** One side of the board's bounds: north is the highest rank, east the highest file. */
export type BoardEdge = "north" | "east" | "south" | "west";

/**
 * The factions, as `Position.kingKind` packs them: one table, keyed by name,
 * and the number is the word. Numbers because it packs into a typed array, is
 * in the hash as a digit, and the codec carries it without knowing what it
 * means. **Only the factory army is left** (§31 R2); 0 and 2–7 were the
 * retired army styles and stay unused — a wire value is never renumbered.
 */
export const KING_KIND = {
  factory: 1
} as const;
export type KingKindName = keyof typeof KING_KIND;
export type KingKind = (typeof KING_KIND)[KingKindName];

export const KING_FACTORY: KingKind = KING_KIND.factory;

/**
 * Every square in a rectangle, rank by rank then file by file — "board
 * order". Two clients derive state independently from one log, so an
 * unordered sweep is a desync (PLAN §5.5).
 */
export function squaresIn(b: Bounds): number[] {
  const out: number[] = [];
  for (let rank = b.minRank; rank <= b.maxRank; rank++) {
    for (let file = b.minFile; file <= b.maxFile; file++) out.push(square(file, rank));
  }
  return out;
}

/** The classic eight-by-eight, as the set of squares it is. */
export const classicCells = (): Set<number> => new Set(squaresIn(CLASSIC_BOUNDS));

// --- facing ---------------------------------------------------------------

export type Direction = 0 | 1 | 2 | 3;
export const NORTH: Direction = 0;
export const EAST: Direction = 1;
export const SOUTH: Direction = 2;
export const WEST: Direction = 3;
export const DIRECTIONS: readonly Direction[] = [NORTH, EAST, SOUTH, WEST];
export const DIRECTION_NAMES = ["N", "E", "S", "W"] as const;

/** Square offsets, indexed by Direction. North is +STRIDE — White's forward. */
export const DIR_OFFSET: readonly number[] = [STRIDE, 1, -STRIDE, -1];

/**
 * The way each side marches, and the facing every pawn of that side starts
 * with. White up the board, Black down it, and any further army in from the
 * side it arrives on — a third player's pawns have to advance *somewhere*.
 */
export const FORWARD_FACING: readonly Direction[] = [NORTH, SOUTH, WEST, EAST];

export const forwardFacing = (side: Side): Direction =>
  FORWARD_FACING[side % FORWARD_FACING.length];

// --- names ----------------------------------------------------------------

export const FILE_NAMES = "abcdefgh";

/**
 * a1..h8 inside the classic eight files, and `f<file>r<rank>` outside it —
 * there are only eight letters and the board is no longer eight wide.
 */
export function squareName(sq: number): string {
  const file = fileOf(sq);
  const rank = rankOf(sq);
  if (file >= 0 && file < 8 && rank >= 0 && rank < 8) {
    return `${FILE_NAMES[file]}${rank + 1}`;
  }
  return `f${file}r${rank}`;
}

export function parseSquare(name: string): number {
  const outside = /^f(-?\d+)r(-?\d+)$/.exec(name);
  if (outside) return square(Number(outside[1]), Number(outside[2]));

  const file = FILE_NAMES.indexOf(name[0]);
  const rank = Number(name.slice(1)) - 1;
  if (file < 0 || !Number.isFinite(rank)) throw new Error(`bad square: ${name}`);
  return square(file, rank);
}
